import React, { useState, useEffect, useCallback } from 'react';
import {
  FaSync, FaCheck, FaTimes, FaExclamationTriangle, FaTrash,
  FaRedo, FaHistory, FaCloud, FaChevronDown, FaChevronRight
} from 'react-icons/fa';
import Modal from '../Modal/Modal.jsx';
import syncEngine from '../../offline/SyncEngine';
import onlineDetector from '../../offline/OnlineDetector';
import offlineStorage from '../../offline/OfflineStorage';
import { SyncQueueStore } from '../../offline/db';
import './SyncCenter.scss';

const SYNC_HISTORY_KEY = 'syncCenter_history';
const MAX_HISTORY_ITEMS = 50;

const SyncCenter = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('pending');
  const [queueItems, setQueueItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(onlineDetector.isOnline);
  const [syncHistory, setSyncHistory] = useState([]);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [lastSyncTime, setLastSyncTime] = useState(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await offlineStorage.getPendingQueue();
      setQueueItems(items);
      const savedHistory = localStorage.getItem(SYNC_HISTORY_KEY);
      if (savedHistory) {
        try { setSyncHistory(JSON.parse(savedHistory)); } catch {}
      }
      const savedLastSync = localStorage.getItem('syncCenter_lastSync');
      if (savedLastSync) setLastSyncTime(parseInt(savedLastSync, 10));
    } catch {} finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) loadData();
  }, [isOpen, loadData]);

  useEffect(() => {
    const unsub = syncEngine.onSync(() => loadData());
    return unsub;
  }, [loadData]);

  useEffect(() => {
    const unsub = onlineDetector.onChange(setIsOnline);
    return unsub;
  }, []);

  const addToHistory = useCallback((entry) => {
    setSyncHistory(prev => {
      const newHistory = [entry, ...prev].slice(0, MAX_HISTORY_ITEMS);
      localStorage.setItem(SYNC_HISTORY_KEY, JSON.stringify(newHistory));
      return newHistory;
    });
    localStorage.setItem('syncCenter_lastSync', Date.now().toString());
    setLastSyncTime(Date.now());
  }, []);

  const handleSyncNow = async () => {
    if (isSyncing || !isOnline) return;
    setIsSyncing(true);
    const startTime = Date.now();
    try {
      const result = await syncEngine.syncNow();
      addToHistory({
        id: Date.now(), timestamp: Date.now(), duration: Date.now() - startTime,
        synced: result.synced, failed: result.failed, total: result.total,
        skipped: result.skipped || false, offline: result.offline || false,
      });
      await loadData();
    } catch (err) {
      addToHistory({
        id: Date.now(), timestamp: Date.now(), duration: Date.now() - startTime,
        synced: 0, failed: 0, total: 0, error: err.message,
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRetryItem = async (item) => {
    try {
      await SyncQueueStore.add({ ...item, status: 'pending', retryCount: 0 });
      await SyncQueueStore.delete(item.id);
      await loadData();
      handleSyncNow();
    } catch {}
  };

  const handleRetryAll = async () => {
    const failedItems = queueItems.filter(item => item.status === 'failed');
    for (const item of failedItems) {
      try {
        await SyncQueueStore.add({ ...item, status: 'pending', retryCount: 0 });
        await SyncQueueStore.delete(item.id);
      } catch {}
    }
    await loadData();
    handleSyncNow();
  };

  const handleDeleteItem = async (item) => {
    if (!window.confirm(`Delete this ${item.entityType} ${item.action} from the sync queue?`)) return;
    try {
      await SyncQueueStore.delete(item.id);
      await loadData();
    } catch {}
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear all items from the sync queue? This cannot be undone.')) return;
    for (const item of queueItems) {
      try { await SyncQueueStore.delete(item.id); } catch {}
    }
    await loadData();
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const pendingItems = queueItems.filter(item => item.status === 'pending');
  const failedItems = queueItems.filter(item => item.status === 'failed');

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Never';
    const diff = Date.now() - timestamp;
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleString();
  };

  const renderQueueItem = (item) => {
    const isExpanded = expandedItems.has(item.id);
    const statusIcon = item.status === 'failed'
      ? <FaExclamationTriangle className="sync-center__status-icon sync-center__status-icon--failed" />
      : <FaCloud className="sync-center__status-icon sync-center__status-icon--pending" />;

    return (
      <div key={item.id} className="sync-center__item">
        <div className="sync-center__item-header" onClick={() => toggleExpand(item.id)}>
          <span className="sync-center__item-expand">
            {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
          </span>
          {statusIcon}
          <span className="sync-center__item-type">{item.entityType}</span>
          <span className={`sync-center__item-action sync-center__item-action--${item.action}`}>
            {item.action}
          </span>
          <span className="sync-center__item-meta">
            {formatRelativeTime(item.createdAt)}
            {item.retryCount > 0 && (
              <span className="sync-center__item-retries"> ({item.retryCount} retries)</span>
            )}
          </span>
          <div className="sync-center__item-actions">
            {item.status === 'failed' && (
              <button className="sync-center__btn sync-center__btn--retry"
                onClick={(e) => { e.stopPropagation(); handleRetryItem(item); }} title="Retry">
                <FaRedo />
              </button>
            )}
            <button className="sync-center__btn sync-center__btn--delete"
              onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }} title="Delete">
              <FaTrash />
            </button>
          </div>
        </div>
        {isExpanded && (
          <div className="sync-center__item-details">
            <div className="sync-center__detail-row">
              <span className="sync-center__detail-label">Local ID:</span>
              <span className="sync-center__detail-value">{item.localId}</span>
            </div>
            <div className="sync-center__detail-row">
              <span className="sync-center__detail-label">Status:</span>
              <span className={`sync-center__detail-value sync-center__detail-value--${item.status}`}>
                {item.status}
              </span>
            </div>
            {item.lastError && (
              <div className="sync-center__detail-row">
                <span className="sync-center__detail-label">Error:</span>
                <span className="sync-center__detail-value sync-center__detail-value--error">{item.lastError}</span>
              </div>
            )}
            <div className="sync-center__detail-row">
              <span className="sync-center__detail-label">Data:</span>
              <pre className="sync-center__detail-data">{JSON.stringify(item.data, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderHistoryItem = (item) => (
    <div key={item.id} className="sync-center__history-item">
      <div className="sync-center__history-time">{formatTime(item.timestamp)}</div>
      <div className="sync-center__history-stats">
        {item.error ? (
          <span className="sync-center__history-error">Failed: {item.error}</span>
        ) : item.skipped || item.offline ? (
          <span className="sync-center__history-skipped">Skipped (offline)</span>
        ) : (
          <span>
            <FaCheck className="sync-center__history-icon sync-center__history-icon--success" />
            {item.synced} synced
            {item.failed > 0 && <span className="sync-center__history-failed">, {item.failed} failed</span>}
          </span>
        )}
      </div>
      <div className="sync-center__history-duration">{item.duration || 0}ms</div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Sync Center" size="large" className="sync-center">
      <div className="sync-center__container">
        <div className="sync-center__status-bar">
          <div className="sync-center__status-info">
            <span className={`sync-center__online-status ${isOnline ? 'sync-center__online-status--online' : 'sync-center__online-status--offline'}`}>
              {isOnline ? <FaCloud /> : <FaCloud className="sync-center__offline-icon" />}
              {isOnline ? ' Online' : ' Offline'}
            </span>
            <span className="sync-center__last-sync">Last sync: {formatRelativeTime(lastSyncTime)}</span>
          </div>
          <button className="sync-center__sync-btn" onClick={handleSyncNow} disabled={isSyncing || !isOnline}>
            <FaSync className={isSyncing ? 'sync-center__sync-icon--spinning' : ''} />
            {isSyncing ? ' Syncing...' : ' Sync Now'}
          </button>
        </div>

        <div className="sync-center__tabs">
          <button className={`sync-center__tab ${activeTab === 'pending' ? 'sync-center__tab--active' : ''}`}
            onClick={() => setActiveTab('pending')}>Pending ({pendingItems.length})</button>
          <button className={`sync-center__tab ${activeTab === 'failed' ? 'sync-center__tab--active' : ''}`}
            onClick={() => setActiveTab('failed')}>Failed ({failedItems.length})</button>
          <button className={`sync-center__tab ${activeTab === 'history' ? 'sync-center__tab--active' : ''}`}
            onClick={() => setActiveTab('history')}><FaHistory /> History</button>
        </div>

        <div className="sync-center__content">
          {isLoading ? (
            <div className="sync-center__loading">Loading...</div>
          ) : (
            <>
              {activeTab === 'pending' && (
                <div className="sync-center__list">
                  {pendingItems.length === 0 ? (
                    <div className="sync-center__empty"><FaCheck className="sync-center__empty-icon" /><p>No pending items</p></div>
                  ) : (
                    <>{pendingItems.map(renderQueueItem)}</>
                  )}
                </div>
              )}
              {activeTab === 'failed' && (
                <div className="sync-center__list">
                  {failedItems.length === 0 ? (
                    <div className="sync-center__empty"><FaCheck className="sync-center__empty-icon" /><p>No failed items</p></div>
                  ) : (
                    <>
                      <div className="sync-center__list-header">
                        <span>{failedItems.length} item{failedItems.length !== 1 ? 's' : ''} failed</span>
                        <div className="sync-center__list-actions">
                          <button className="sync-center__btn sync-center__btn--retry-all" onClick={handleRetryAll}><FaRedo /> Retry All</button>
                          <button className="sync-center__btn sync-center__btn--clear" onClick={handleClearAll}><FaTrash /> Clear All</button>
                        </div>
                      </div>
                      {failedItems.map(renderQueueItem)}
                    </>
                  )}
                </div>
              )}
              {activeTab === 'history' && (
                <div className="sync-center__history">
                  {syncHistory.length === 0 ? (
                    <div className="sync-center__empty"><FaHistory className="sync-center__empty-icon" /><p>No sync history yet</p></div>
                  ) : (
                    syncHistory.map(renderHistoryItem)
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SyncCenter;
